import { hasCompleteBillingDetails } from '@/components/billing/BillingDetailsForm'
import { profileCards } from '@/features/subscribe/card-step'
import { useAuth } from '@/lib'
import { logger } from '@/ui'
import {
	organisationIndexQueryKey,
	useEntitlementIndex,
	useOrganisationIndex,
	useOrganisationRename,
	useOrganisationStore,
	usePaymentMethodIndex,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { trialOrganisation, type TrialSetupProgress } from './next-unanswered-step'
import type { CompanyDetails } from './screens/company-screen'

/**
 * The company, billing profile and card a self-signup sets up in onboarding, read back from Nexus so a
 * reload resumes at the same step. The session is switched into the company, so billing calls run as its admin.
 */
export function useTrialSetup(isSelfSignup: boolean) {
	const { switchOrganisation } = useAuth()
	const queryClient = useQueryClient()
	const createOrganisation = useOrganisationStore()
	const renameOrganisation = useOrganisationRename()
	const [enteredOrganisationId, setEnteredOrganisationId] = useState<string | null>(null)
	const enteringOrganisationIdRef = useRef<string | null>(null)
	const [enterFailed, setEnterFailed] = useState(false)

	const organisations = useOrganisationIndex({}, { enabled: isSelfSignup })
	const organisation = trialOrganisation(organisations.data ?? [])
	const isEntered = organisation !== null && enteredOrganisationId === organisation.id

	const profiles = useProfilesIndex(organisation?.id ?? '', { enabled: isEntered })
	const profile = profiles.data?.data.find((candidate) => candidate.isPrimary) ?? profiles.data?.data[0] ?? null

	const paymentMethods = usePaymentMethodIndex(organisation?.id ?? '', undefined, { enabled: isEntered })
	const cards = profile ? profileCards(paymentMethods.data?.data ?? [], profile) : []

	const entitlements = useEntitlementIndex(organisation?.id ?? '', { enabled: isEntered })

	useEffect(() => {
		if (
			!organisation?.workosId
			|| enteredOrganisationId === organisation.id
			|| enteringOrganisationIdRef.current === organisation.id
		) { return }

		enteringOrganisationIdRef.current = organisation.id
		switchOrganisation(organisation.workosId)
			.then(() => setEnteredOrganisationId(organisation.id))
			.catch((error: unknown) => {
				logger.error(error, { category: 'onboarding', data: { action: 'enter-organisation' } })
				setEnterFailed(true)
			})
			.finally(() => {
				enteringOrganisationIdRef.current = null
			})
	}, [organisation, enteredOrganisationId, switchOrganisation])

	const saveCompany = async ({ name, website }: CompanyDetails) => {
		if (organisation) {
			if (name !== organisation.name) {
				await renameOrganisation.mutateAsync({ organisation: organisation.id, data: { name } })
				await queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() })
			}
			return
		}

		const created = await createOrganisation.mutateAsync({ data: { name, website } })

		enteringOrganisationIdRef.current = created.id
		try {
			if (created.workosId) {
				await switchOrganisation(created.workosId)
			}
			setEnteredOrganisationId(created.id)
		} finally {
			enteringOrganisationIdRef.current = null
		}
		await queryClient.invalidateQueries({ queryKey: organisationIndexQueryKey() })
	}

	const isLoading = isSelfSignup && (
		organisations.isLoading
		|| (organisation !== null && !isEntered && !enterFailed)
		|| profiles.isLoading
		|| paymentMethods.isLoading
		|| entitlements.isLoading
	)

	const progress: TrialSetupProgress = {
		hasCompany: organisation !== null,
		hasBillingDetails: profile !== null && hasCompleteBillingDetails(profile),
		hasCard: cards.length > 0,
		hasAccess: entitlements.data?.subscriptionAccess.hasAccess ?? false,
	}

	return {
		organisation,
		profile,
		cards,
		progress,
		isLoading,
		enterFailed,
		saveCompany,
		isSavingCompany: createOrganisation.isPending || renameOrganisation.isPending,
	}
}
